/**
 * Rules for changing many order items' status at once.
 *
 * Kept separate from the toolbar so the eligibility maths — the part that
 * decides what an admin is actually about to change — is unit-testable.
 */

import { bulkApproveBodyFor, isBulkApproveSource } from './bulkApprove';

/**
 * Statuses the per-item status endpoint accepts as a *starting* point.
 * Mirrors UPDATABLE_STATUSES in server/src/routes/orgOrderItems.ts; anything
 * else (PENDING and WAITLIST await approval, PICKED_UP is terminal there) is
 * rejected by the API, so it is filtered out before we send anything.
 */
export const BULK_STATUS_ELIGIBLE_SOURCES = [
  'APPROVED',
  'ASSIGNED',
  'READY_FOR_PICKUP',
  'ENROUTE',
  'PREPAY_REQUIRED',
  'DELIVERED',
  'INVOICED',
  'RAILCAR',
  'CUSTOMER_CANCELLED',
];

/**
 * Statuses offered as a bulk *target*: the normal dispatch-to-paid progression.
 *
 * Rejection and customer cancellation are left out on purpose — both are
 * destructive and rejection carries a reason, so they stay per-load flows.
 */
export const BULK_STATUS_TARGETS = [
  { value: 'APPROVED', label: 'Accepted' },
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'READY_FOR_PICKUP', label: 'Ready for Pickup' },
  { value: 'ENROUTE', label: 'Enroute' },
  { value: 'PICKED_UP', label: 'Picked Up' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'INVOICED', label: 'Invoiced' },
  { value: 'PAID', label: 'Paid' },
];

/** Target statuses that trigger a customer-facing email on the server. */
// Ready for Pickup is not here: its email is sent explicitly from the load
// (Notify customer), never by a status change.
const CUSTOMER_NOTIFYING_TARGETS = ['ENROUTE', 'DELIVERED'];

export function notifiesCustomer(status: string): boolean {
  return CUSTOMER_NOTIFYING_TARGETS.includes(status);
}

export function statusLabel(status: string): string {
  return BULK_STATUS_TARGETS.find((s) => s.value === status)?.label ?? status;
}

export interface BulkStatusRow {
  id: string;
  status?: string;
  loadNumber?: string | null;
  orderId?: string;
  order?: { id?: string };
  pickupDate?: string | null;
}

export interface BulkStatusPlan<T> {
  /** Rows that will be sent to the API. */
  eligible: T[];
  /** Rows already sitting in the target status — nothing to do. */
  alreadyInStatus: T[];
  /** Rows the status endpoint refuses (awaiting approval, or picked up). */
  notAllowed: T[];
  /**
   * Pending rows that could be approved in bulk, except the load is missing a
   * warehouse, fulfillment method or pallet size. They need the row form, so
   * they're reported separately from loads that are simply the wrong status.
   */
  missingApprovalDetails: T[];
  /**
   * Rows targeting READY_FOR_PICKUP with no pickup date. The server requires one
   * (the pickup-deadline email derives from it), so they're set aside up front
   * rather than failing per-row.
   */
  missingPickupDate: T[];
}

/**
 * Splits the selection into what will change and what will be skipped, so the
 * confirmation can say so up front instead of surfacing per-row API errors.
 *
 * Approving is the one target with two routes behind it: a pending load reaches
 * `APPROVED` through the approve endpoint, everything else through the status
 * endpoint. Both land in `eligible` — `bulkApproveTargets` decides which is
 * which when the requests are actually built.
 */
export function planBulkStatusChange<T extends BulkStatusRow>(
  rows: T[],
  targetStatus: string
): BulkStatusPlan<T> {
  const plan: BulkStatusPlan<T> = {
    eligible: [],
    alreadyInStatus: [],
    notAllowed: [],
    missingApprovalDetails: [],
    missingPickupDate: [],
  };

  for (const row of rows) {
    const status = row.status ?? '';

    if (status === targetStatus) {
      plan.alreadyInStatus.push(row);
    } else if (targetStatus === 'APPROVED' && isBulkApproveSource(status)) {
      if (bulkApproveBodyFor(row, targetStatus)) {
        plan.eligible.push(row);
      } else {
        plan.missingApprovalDetails.push(row);
      }
    } else if (!BULK_STATUS_ELIGIBLE_SOURCES.includes(status)) {
      plan.notAllowed.push(row);
    } else if (targetStatus === 'READY_FOR_PICKUP' && !row.pickupDate) {
      plan.missingPickupDate.push(row);
    } else {
      plan.eligible.push(row);
    }
  }

  return plan;
}
